import {announce} from '@github-ui/aria-live'
import type {CachesIndexPayload} from '@github-ui/dotcom-schema/types/actions/v1/caches_index'
import {addFilterValue, getFilterValues, MAX_QUERY_LENGTH, removeFilterValue} from '@github-ui/filter-query'
import {ListFilterButton} from '@github-ui/list-filter-button'
import {useQuery} from '@github-ui/react-query'
import {SimpleListHeader} from '@github-ui/simple-list-view/SimpleListHeader'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {ActionList, ActionMenu, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useEffect, useId, useRef, useState} from 'react'
import {cacheSortOptions} from './cache-filter-options'
import styles from './CachesList.module.css'

const allBranches = {id: '', text: 'All branches'}
const queryTooLongMessage =
  'Filter menus are unavailable because this query is too long. Shorten it in the filter input.'
const selectionTooLongMessage = 'Adding this filter would make your query too long. Use the filter input instead.'
const selectionTooLongNotice = {variant: 'warning', text: selectionTooLongMessage} as const

function announceSelectionTooLong(element: HTMLSpanElement | null) {
  if (element) announce(selectionTooLongMessage, {element})
}

export function CachesListHeader({
  payload,
  query,
  onQueryChange,
  track,
}: {
  payload: CachesIndexPayload
  query: string
  onQueryChange: (query: string) => void
  track: (action: string) => void
}) {
  const {query: canonicalQuery, totalCount} = payload
  const count = `${totalCount.toLocaleString('en-US')} ${totalCount === BigInt(1) ? 'cache' : 'caches'}`
  const previousQueryRef = useRef(canonicalQuery)
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Announcements follow completed responses, not input events.
    if (previousQueryRef.current === canonicalQuery) return
    previousQueryRef.current = canonicalQuery
    announce(`${count} found.`)
  }, [count, canonicalQuery])

  const unavailableMessageId = useId()
  const [rejectedFilter, setRejectedFilter] = useState<{query: string; key: 'branch' | 'sort'}>()
  if (rejectedFilter && rejectedFilter.query !== query) setRejectedFilter(undefined)
  const queryTooLong = query.length > MAX_QUERY_LENGTH
  const rejectedKey = rejectedFilter?.query === query ? rejectedFilter.key : undefined
  const branch = getFilterValues(query, {key: 'branch'}).at(-1) ?? ''
  const sort = getFilterValues(query, {key: 'sort'}).at(-1) || 'accessed-desc'
  const updateFilter = (key: 'branch' | 'sort', value: string) => {
    if (queryTooLong) return false
    let nextQuery = query
    for (const current of getFilterValues(query, {key})) nextQuery = removeFilterValue(nextQuery, current, {key})
    if (value) {
      const updatedQuery = addFilterValue(nextQuery, value, {key})
      if (updatedQuery === nextQuery) {
        setRejectedFilter({query, key})
        return false
      }
      nextQuery = updatedQuery
    }
    setRejectedFilter(undefined)
    if (nextQuery === query) return true
    track(`filter_${key}`)
    onQueryChange(nextQuery)
    return true
  }

  return (
    <SimpleListHeader className={styles.Header}>
      <span className={styles.Count}>{count}</span>
      <div className={styles.Filters}>
        {queryTooLong ? (
          <>
            <ListFilterButton
              aria-haspopup="dialog"
              aria-expanded={false}
              aria-disabled="true"
              aria-describedby={unavailableMessageId}
              inactive
            >
              Branch
            </ListFilterButton>
            <ListFilterButton
              aria-haspopup="menu"
              aria-expanded={false}
              aria-disabled="true"
              aria-describedby={unavailableMessageId}
              inactive
            >
              Sort
            </ListFilterButton>
          </>
        ) : (
          <>
            <CachesBranchFilter
              path={payload.branchesPath}
              value={branch}
              onChange={value => updateFilter('branch', value)}
              selectionTooLong={rejectedKey === 'branch'}
            />
            <ActionMenu>
              <ActionMenu.Anchor>
                <ListFilterButton aria-haspopup="menu" aria-expanded={false}>
                  Sort
                </ListFilterButton>
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList>
                  <ActionList.Group selectionVariant="single">
                    <ActionList.GroupHeading>Sort by</ActionList.GroupHeading>
                    {cacheSortOptions.map(option => (
                      <ActionList.Item
                        key={option.id}
                        role="menuitemradio"
                        selected={sort === option.id}
                        onSelect={event => {
                          if (!updateFilter('sort', option.id)) event.preventDefault()
                        }}
                      >
                        {option.text}
                      </ActionList.Item>
                    ))}
                  </ActionList.Group>
                </ActionList>
                {rejectedKey === 'sort' && (
                  <InlineMessage variant="warning" className={styles.Text}>
                    <span ref={announceSelectionTooLong}>{selectionTooLongMessage}</span>
                  </InlineMessage>
                )}
              </ActionMenu.Overlay>
            </ActionMenu>
          </>
        )}
      </div>
      {queryTooLong && (
        <InlineMessage id={unavailableMessageId} variant="warning" className={styles.Text}>
          {queryTooLongMessage}
        </InlineMessage>
      )}
    </SimpleListHeader>
  )
}

function CachesBranchFilter({
  path,
  value,
  onChange,
  selectionTooLong,
}: {
  path: string
  value: string
  onChange: (value: string) => boolean
  selectionTooLong: boolean
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  // This legacy JSON picker endpoint returns a string array and is only fetched while the menu is open.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema
  const branches = useQuery({
    queryKey: ['cache-branches', path],
    enabled: open && Boolean(path),
    queryFn: async () => {
      const response = await reactFetchJSON(path)
      if (!response.ok) throw new Error('Unable to load branches.')
      const data: unknown = await response.json()
      if (!Array.isArray(data) || !data.every((item): item is string => typeof item === 'string')) {
        throw new Error('Unable to load branches.')
      }
      return data
    },
    staleTime: 60_000,
  })
  useEffect(() => {
    if (branches.isError) announce('Unable to load branches. Try again.')
  }, [branches.isError])
  const selected = value ? {id: value, text: value} : allBranches
  const names = [...new Set([value, ...(branches.data ?? [])].filter(Boolean))]
  const items = [allBranches, ...names.map(name => ({id: name, text: name}))].filter(item =>
    item.text.toLowerCase().includes(filter.toLowerCase()),
  )

  return (
    <SelectPanel
      title="Filter by branch"
      inputLabel="Find a branch"
      open={open}
      onOpenChange={(nextOpen, gesture) => {
        // Selection closes only after the replacement query passes the length check.
        if (gesture === 'selection') return
        setOpen(nextOpen)
        if (!nextOpen) setFilter('')
      }}
      filterValue={filter}
      onFilterChange={setFilter}
      items={items}
      virtualized
      selected={selected}
      onSelectedChange={(item: SelectPanelItemInput | undefined) => {
        if (item && !onChange(String(item.id))) return
        setOpen(false)
        setFilter('')
      }}
      notice={selectionTooLong ? selectionTooLongNotice : undefined}
      loading={branches.isFetching}
      message={
        branches.isError
          ? {variant: 'error', title: 'Unable to load branches', body: 'Close and reopen the filter to try again.'}
          : undefined
      }
      renderAnchor={({children: _children, ...anchorProps}) => (
        <ListFilterButton {...anchorProps} aria-haspopup="dialog" aria-expanded={open}>
          Branch
        </ListFilterButton>
      )}
    />
  )
}
